import React from 'react';
import {
  Building,
  Target,
  ShieldCheck,
  MapPin,
  Cpu,
  Users,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  HeartHandshake,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';

export const AboutPage: React.FC = () => {
  const { navigateTo } = useApp();

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8 space-y-12">
      {/* Hero Header */}
      <div className="max-w-5xl mx-auto bg-slate-900 text-white rounded-3xl p-6 sm:p-12 border border-slate-800 shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
        <div className="relative max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
            <Building className="w-4 h-4 text-emerald-400" />
            <span>Built for Nigeria</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
            About JobMatch NG
          </h1>

          <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
            JobMatch NG is a Nigerian technology platform designed to bridge the trust gap in skilled trades.
            We connect homeowners, property managers, and businesses with vetted local professionals, technicians,
            and craftspeople.
          </p>
        </div>
      </div>

      {/* Mission & Purpose */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
            <Target className="w-3.5 h-3.5 text-emerald-600" />
            <span>Our Purpose</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            Solving the Everyday Challenge of Finding Dependable Professionals
          </h2>

          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
            In many Nigerian cities, finding a competent plumber, electrician, generator technician, or
            carpenter often relies on random roadside workshops or word-of-mouth recommendations with no
            record of past performance or verified identity.
          </p>

          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
            JobMatch NG was created to bring structure, transparency, and accountability to technical service
            delivery. We provide skilled craftspeople with the digital presence they deserve while giving customers
            the peace of mind that comes from verified identities and transparent, itemized quotes.
          </p>
        </div>

        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xs space-y-4">
          <h3 className="font-extrabold text-slate-900 text-base">Core Principles Guiding JobMatch NG</h3>
          <ul className="space-y-3 text-xs sm:text-sm text-slate-700">
            <li className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span>
                <strong>Identity Accountability:</strong> Verifying government credentials, physical operating base,
                and biometric facial liveness.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span>
                <strong>Transparent Pricing:</strong> Encouraging itemized quotations separating labor fees from
                material estimates.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span>
                <strong>Local Relevance:</strong> Matching by Nigerian State and Local Government Area (LGA)
                to minimize travel times and eliminate arbitrary transit surcharges.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span>
                <strong>Dignity of Labor:</strong> Empowering hard-working Nigerian craftspeople to build sustainable
                commercial reputations on verified merits.
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* Why Local Matching Matters */}
      <div className="max-w-5xl mx-auto bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-xs space-y-6">
        <div className="max-w-3xl space-y-2">
          <div className="flex items-center gap-2 text-emerald-700 font-bold text-xs">
            <MapPin className="w-4 h-4 text-emerald-600" />
            <span>Hyper-Local Matching Across Nigeria</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
            Why Matching by State and LGA Matters
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
            Traffic conditions and distance in Nigerian metropolitan hubs like Lagos, Ibadan, Port Harcourt,
            and Abuja often determine whether an urgent repair is solved in two hours or delayed for two days.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
          <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
            <h4 className="font-bold text-sm text-slate-900">Rapid Response</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              When an urgent pipe bursts or an electrical panel sparks, local professionals in your LGA can reach
              your site quickly.
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
            <h4 className="font-bold text-sm text-slate-900">Lower Transport Overhead</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Eliminates excessive transit costs that distant technicians inevitably factor into labor bills.
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
            <h4 className="font-bold text-sm text-slate-900">Neighborhood Knowledge</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Local professionals understand local water pressure patterns, distribution board challenges, and
              nearby hardware supply markets.
            </p>
          </div>
        </div>
      </div>

      {/* How Tech Improves Discovery */}
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            Technology with a Purpose
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 max-w-xl mx-auto">
            We use modern web tools and automated job classification to reduce friction, avoid misunderstandings,
            and keep transactions transparent.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs space-y-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <Cpu className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-base text-slate-900">Automated Job Analysis</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Categorizes technical work, flags trade complexity, and highlights safety considerations for both
              client and professional.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs space-y-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-base text-slate-900">Biometric Liveness Verification</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              State-of-the-art camera liveness check ensures that the individual quoting on your home matches the
              verified government record.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs space-y-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
              <HeartHandshake className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-base text-slate-900">Paystack Billing Security</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              PCI-DSS certified gateway integration ensuring safe, standard Naira payments with automated
              receipt tracking.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto bg-slate-900 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-xl">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
          Join the Future of Technical Trades in Nigeria
        </h2>
        <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto leading-relaxed">
          Whether you need a job done right today or want to showcase your craftsmanship, JobMatch NG is
          here to help.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => navigateTo('post_job')}
            className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-bold shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <span>Post a Job Free</span>
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigateTo('become_pro')}
            className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs sm:text-sm font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <span>Become a Professional</span>
          </button>
        </div>
      </div>
    </div>
  );
};
